import { useEffect, useState } from "react";
import api from "../lib/api";

export const ADMIN_HEALTH_POLL_INTERVAL_MS = 30_000;

const COMPONENT_NAMES = {
  scheduler: "Scheduler",
  scan_workers: "Scan workers",
};

const UNKNOWN_HEALTH = {
  state: "unknown",
  label: "Unknown",
  detail: "Health signal unavailable",
};

function componentName(key) {
  return COMPONENT_NAMES[key] || key.replaceAll("_", " ");
}

export function healthPresentation(payload) {
  if (!payload || typeof payload !== "object") return UNKNOWN_HEALTH;

  if (payload.status === "healthy") {
    return {
      state: "healthy",
      label: "Healthy",
      detail: "All monitored components healthy",
    };
  }

  if (payload.status === "degraded") {
    const degradedComponents = Object.entries(payload.workers || {})
      .filter(([, check]) => check?.status === "degraded")
      .map(([key]) => componentName(key));
    if (payload.database && payload.database !== "connected") {
      degradedComponents.unshift("Database");
    }

    const components = degradedComponents.length
      ? degradedComponents.join(", ")
      : "Unidentified component";
    return {
      state: "degraded",
      label: `Degraded · ${components}`,
      detail: `${components} reported degraded`,
    };
  }

  return UNKNOWN_HEALTH;
}

export async function readAdminHealth(client = api) {
  try {
    const response = await client.get("/health");
    return healthPresentation(response.data);
  } catch (error) {
    // The authoritative health endpoint intentionally returns 503 when a
    // monitored worker is degraded. Axios rejects that response, so retain its
    // component-level health payload rather than misclassifying it as unknown.
    if (error?.response?.status === 503) {
      const presentation = healthPresentation(error.response.data);
      if (presentation.state === "degraded") return presentation;
    }
    return UNKNOWN_HEALTH;
  }
}

function checkedTime(value) {
  if (!value) return "Not yet checked";
  return `Checked ${value.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  })}`;
}

const STATE_STYLES = {
  healthy: {
    badge: "border-emerald-300 bg-emerald-50 text-emerald-800",
    dot: "bg-emerald-500",
  },
  degraded: {
    badge: "border-amber-300 bg-amber-50 text-amber-900",
    dot: "bg-amber-500",
  },
  unknown: {
    badge: "border-slate-300 bg-slate-100 text-slate-700",
    dot: "bg-slate-400",
  },
};

export default function AdminHealthStatus({
  client = api,
  pollInterval = ADMIN_HEALTH_POLL_INTERVAL_MS,
}) {
  const [health, setHealth] = useState(UNKNOWN_HEALTH);
  const [checkedAt, setCheckedAt] = useState(null);

  useEffect(() => {
    let active = true;

    const check = async () => {
      const next = await readAdminHealth(client);
      if (!active) return;
      setHealth(next);
      setCheckedAt(new Date());
    };

    check();
    const interval = window.setInterval(check, pollInterval);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [client, pollInterval]);

  const styles = STATE_STYLES[health.state];
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="admin-health-status"
      className="flex min-w-0 flex-col items-end gap-1"
    >
      <span className={`flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider ${styles.badge}`}>
        <span className={`h-2 w-2 shrink-0 rounded-full ${styles.dot}`} />
        <span className="truncate">{health.label}</span>
      </span>
      <span className="max-w-56 truncate text-right text-[10px] text-slate-500" title={`${health.detail}. ${checkedTime(checkedAt)}`}>
        {health.detail} · {checkedTime(checkedAt)}
      </span>
    </div>
  );
}
